import axios from "axios";
import { jwtDecode } from "jwt-decode";

// Determine API URL based on environment
const getApiUrl = () => {
  // In production (when served from agritur.dk), use the production API
  if (window.location.hostname === 'agritur.dk' || window.location.hostname === 'www.agritur.dk') {
    return 'https://api.agritur.dk';
  }
  // In development, use localhost
  return 'http://localhost:8001';
};

const apiClient = axios.create({
  baseURL: getApiUrl(),
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue = [];

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue requests while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }
        
        // Check if refresh token is valid and not expired
        let decoded;
        try {
          decoded = jwtDecode(refreshToken);
        } catch (decodeErr) {
          throw new Error('Invalid refresh token');
        }
        
        // Check expiration separately after successful decode
        if (decoded.exp * 1000 <= Date.now()) {
          throw new Error('Refresh token expired');
        }
        
        const response = await axios.post(`${getApiUrl()}/refresh-token`, {
          refresh_token: refreshToken
        });
        const { access_token } = response.data;
        
        localStorage.setItem('access_token', access_token);
        
        // Retry queued requests
        failedQueue.forEach(prom => prom.resolve(access_token));
        failedQueue = [];
        
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed - clear auth and redirect
        failedQueue.forEach(prom => prom.reject(refreshError));
        failedQueue = [];
        
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login?session_expired=true';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

// Game key API functions
export const generateGameKey = async () => {
  const response = await apiClient.post('/game-key/generate');
  return response.data;
};

export const getGameKey = async () => {
  const response = await apiClient.get('/game-key');
  return response.data;
};

export default apiClient;